import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { BaseComponent } from '../base/base.component';
import { ButtonComponent } from '../core/buttons/button.component';
import { InputComponent } from '../core/controls/input.component';
import { PasswordRequirementsComponent } from '../core/controls/password-requirements.component';
import { ApiErrorLabelComponent } from '../core/labels/api-error-label.component';
import { ActionRowComponent } from '../core/panels/action-row.component';
import { RequiredFieldsNoteComponent } from '../core/labels/required-fields-note.component';
@Component({
  selector: 'app-password-reset',
  imports: [
    ButtonComponent,
    RouterModule,
    ActionRowComponent,
    InputComponent,
    PasswordRequirementsComponent,
    ReactiveFormsModule,
    ApiErrorLabelComponent,
    RequiredFieldsNoteComponent,
  ],
  template: `
    <form
      class="tw-px-4 tw-pt-4 tw-pb-2 "
      [formGroup]="fg"
      (ngSubmit)="submitPassword()"
    >
      @if (error) {
        <div class="tw-flex tw-justify-center tw-pb-4 tw-pt-4">
          <app-api-error-label [error]="error" />
        </div>
      }
      @if (this.page === 'Expired') {
        <div class="tw-font-bold tw-py-4">Password Expired</div>
        <div>
          Your password has expired and will need to be reset. Please make note
          of your new password before proceeding.
        </div>
      } @else {
        <div class="tw-font-bold tw-pt-4 tw-pb-4">Password Reset</div>
        <div>Please make note of your new password before proceeding.</div>
      }

      <app-required-fields-note />
      <div class="tw-pt-8">
        <app-input
          type="Password"
          [control]="fg.controls.password"
          label="New password"
          [large]="true"
          [showReveal]="true"
        />
      </div>
      <div class="tw-pb-2 tw-pt-3">
        <app-input
          type="Password"
          [control]="fg.controls.confirmPassword"
          label="Retype new password"
          [large]="true"
          [showReveal]="true"
        />
      </div>
      <div class="tw-p-2">
        <app-password-requirements [error]="passwordError" />
      </div>

      <div class=" tw-py-10">
        <app-action-row>
          <app-button
            type="submit"
            [disabled]="!fg.valid"
            right1
            label="Submit"
            [loading]="loading"
          ></app-button>
          <app-button
            right2
            label="Cancel"
            emphasis="Low"
            routerLink="/login"
          ></app-button>
        </app-action-row>
      </div>
    </form>
  `,
  styles: ``,
})
export class PasswordResetComponent extends BaseComponent implements OnInit {
  loading = false;
  token: string;
  userName: string;
  status: string;
  error: any;
  page: string;
  passwordError = false;

  fg = new FormGroup(
    {
      password: new FormControl<string>(null, [
        Validators.required,
        Validators.minLength(8),
      ]),
      confirmPassword: new FormControl<string>(null, [Validators.required]),
    },
    this.confirmPassword(),
  );

  ngOnInit(): void {
    const reset: any = history.state.pwReset;
    if (!reset) {
      this.router.navigate(['/login']);
      return;
    }
    this.token = reset.token;
    this.userName = reset.userName;
    this.page = reset.result;
  }

  async submitPassword() {
    this.loading = true;
    this.passwordError = false;

    if (!this.fg.valid) {
      this.fg.markAllAsTouched();
      this.loading = false;
      return;
    }

    try {
      const results = await this.api.auth.passwordReset({
        password: this.fg.value.password,
        token: this.token,
      });

      if (results.result === 'Success') {
        this.router.navigate(['/login'], {
          queryParams: { passwordReset: true },
        });
      } else if (results.result === 'Expired') {
        this.router.navigate(['/login'], {
          queryParams: { passwordResetExpired: true },
        });
      } else {
        this.passwordError = true;
      }

      this.status = results.result;
    } catch (e) {
      this.error = e;
      if (this.error.error.type == 'SecurityTokenExpiredException') {
        this.router.navigate(['/login'], {
          queryParams: { passwordResetExpired: true },
        });
      }
    } finally {
      this.loading = false;
    }
  }

  confirmPassword(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      return this.fg?.controls.password.value ===
        this.fg?.controls.confirmPassword.value
        ? (control.get('confirmPassword')?.setErrors(null), null)
        : (control
            .get('confirmPassword')
            ?.setErrors({ mismatch: 'Does not match' }),
          { mismatch: true });
    };
  }
}
